import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { ZegoExpressEngine } from 'zego-express-engine-webrtc';
import {
  ZegoStreamList,
  ZegoStreamUpdateType,
} from 'zego-express-engine-webrtc/sdk/code/zh/ZegoExpressEntity.web';
import ZegoLocalStream from 'zego-express-engine-webrtc/sdk/code/zh/ZegoLocalStream.web';
import {
  ZegoCaptureCamera,
  ZegoCaptureMicrophone,
  ZegoStreamOptions,
} from 'zego-express-engine-webrtc/sdk/src/common/zego.entity.web';

import { retry } from '../utils/utils';
import { ZegoEngineService } from './zego-engine.service';

/**
 * Cấu hình capture dùng chung cho lúc tạo stream và lúc thêm track vào stream đang publish,
 * để track thêm sau có cùng thông số với track được tạo ban đầu.
 */
const CAMERA_VIDEO_CONFIG: ZegoCaptureCamera = {
  quality: 4,
  width: 960,
  height: 540,
  frameRate: 15,
};

const MICROPHONE_AUDIO_CONFIG: ZegoCaptureMicrophone = {
  ANS: true,
  AGC: true,
  AEC: true,
};

/**
 * SDK chỉ khai báo `ZegoStreamUpdateType` trong file .d.ts, bundle runtime không export enum
 * này nên phải dùng lại giá trị số của nó: video = 0, audio = 1, videoAndAudio = 2.
 */
const STREAM_UPDATE_TYPE = {
  video: 0 as ZegoStreamUpdateType,
  audio: 1 as ZegoStreamUpdateType,
};

export type VideoPreset = Parameters<ZegoExpressEngine['setVideoConfig']>[1];

//Done Refactor
//Quản lý STREAM
@Injectable({
  providedIn: 'root',
})
export class ZegoStreamService implements OnDestroy {
  private readonly zegoEngineService = inject(ZegoEngineService);

  private engine = signal<ZegoExpressEngine | undefined>(this.zegoEngineService.engine);
  private engineDestructionSubscription: Subscription | null = null;

  currentMicDeviceID = signal<string | null>(null);
  currentCamDeviceID = signal<string | null>(null);

  localStream = signal<{ stream: ZegoLocalStream; streamID: string } | null>(null);

  /**
   * Preset video do màn họp chọn theo số người. Lưu lại để áp dụng lại mỗi khi stream được
   * tạo mới hoặc bật lại cam, vì lúc đó SDK dùng lại CAMERA_VIDEO_CONFIG mặc định.
   */
  private videoPreset: VideoPreset | null = null;

  mapStream = signal<Map<string, ZegoStreamList>>(new Map());
  screenSharedStreams = signal<Map<string, ZegoStreamList>>(new Map());

  constructor() {
    this.setupEngineDestructionMonitoring();
  }

  /**
   * Monitor engine destruction and clear local reference
   */
  private setupEngineDestructionMonitoring(): void {
    this.engineDestructionSubscription = this.zegoEngineService.engineDestroyed.subscribe(() => {
      console.warn('[ZegoMeeting] Engine destroyed - clearing local reference');
      // `engine` là signal: phải set giá trị, không gán đè lên chính signal
      // (gán đè làm mọi lời gọi this.engine() ném "is not a function").
      this.engine.set(undefined);
    });
  }

  ngOnDestroy(): void {
    this.engineDestructionSubscription?.unsubscribe();
  }

  /**
   * Validate engine is still operational
   */
  private isEngineValid(): boolean {
    return this.engine() !== undefined;
  }

  // Create a stream and start the preview.
  // After calling the createZegoStream method, you need to wait for the ZEGO server to return the local stream object before any further operation.
  // Publish a stream to ZEGOCLOUD audio and video cloud. You can set `streamID` based on your service requirements but ensure that it is globally unique.
  async createAndPublishStream(
    userID: string,
    isCameraEnabled: boolean,
    isMicEnabled: boolean,
    isScreenStream = false,
    muteTrack: 'video' | 'audio' | 'none' | 'both' = 'none',
    forceCreateBoth = false,
  ): Promise<{ localStream: ZegoLocalStream; streamID: string; seq?: number } | null> {
    if (!isCameraEnabled && !isMicEnabled && !forceCreateBoth && !isScreenStream) return null;
    if (!this.isEngineValid()) {
      console.warn('[ZegoStream] Cannot create stream - engine unavailable');
      return null;
    }
    console.log(
      `[ZegoStream] Creating and publishing stream for user ${userID} - camera: ${isCameraEnabled}, mic: ${isMicEnabled}, screen: ${isScreenStream}, muteTrack: ${muteTrack}, forceCreateBoth: ${forceCreateBoth}`,
    );
    const streamConfig: ZegoStreamOptions = isScreenStream
      ? {
          videoBitrate: 1500,
          screen: {
            audio: {
              ANS: true,
              AGC: true,
              AEC: true,
            },
            video: {
              quality: 4,
              frameRate: 15,
              width: 1280,
              height: 720,
            },
          },
        }
      : {
          camera: {
            video: isCameraEnabled || forceCreateBoth ? { ...CAMERA_VIDEO_CONFIG } : false,
            audio: isMicEnabled || forceCreateBoth ? { ...MICROPHONE_AUDIO_CONFIG } : false,
          },
          videoBitrate: 1200,
          audioBitrate: 48,
        };
    try {
      // Create a stream and start the preview.
      const localStream = await this.engine()!.createZegoStream(streamConfig);
      if (!isScreenStream) {
        this.zegoEngineService.engine.muteMicrophone(false);
      }

      this.setCurrentDeviceId(localStream);
      // Publish a stream to ZEGOCLOUD audio and video cloud. You can set `streamID` based on your service requirements but ensure that it is globally unique.
      const streamID = isScreenStream ? `${userID}_screen` : userID;
      const seq = isScreenStream
        ? this.zegoEngineService.engine.getNetworkTimeInfo().timestamp
        : undefined;

      this.engine()!.startPublishingStream(streamID, localStream, {
        enableTrafficControl: true,
        trafficControlFocusOnMode: 1,
        enableAutoSwitchVideoCodec: true,
        extraInfo: isScreenStream
          ? JSON.stringify({ seq })
          : JSON.stringify({
              isMicEnabled: isMicEnabled || false,
              isCameraEnabled: isCameraEnabled || false,
            }),
      });

      switch (muteTrack) {
        case 'audio':
          this.engine()!.muteMicrophone(true);
          break;
        case 'both':
          this.engine()!.muteMicrophone(true);
          break;
        default:
          break;
      }
      if (!isScreenStream) {
        this.localStream.set({ stream: localStream, streamID: streamID });
        void this.applyVideoPreset();
      }
      return { localStream, streamID, seq };
    } catch (error) {
      console.error('Error creating and publishing stream:', error);
      return null;
    }
  }

  setCurrentDeviceId(localStream: ZegoLocalStream) {
    const micDeviceId = localStream.getAudioTracks()[0]?.getSettings().deviceId ?? null;
    const camDeviceId = localStream.getVideoTracks()[0]?.getSettings().deviceId ?? null;

    this.currentMicDeviceID.set(micDeviceId);
    this.currentCamDeviceID.set(camDeviceId);
  }

  async useMicDevice(deviceId: string) {
    return this.enqueueDeviceSwitch(async () => {
      const stream = this.localStream()?.stream;
      if (!stream) return;

      const engine = this.zegoEngineService.engine;
      const resolvedId = await this.resolveDeviceId('audioinput', deviceId);
      const isMuted = engine.isMicrophoneMuted();
      const result = await engine.useAudioDevice(stream, resolvedId);
      engine.muteMicrophone(isMuted);
      if (result.errorCode !== 0) {
        throw new Error(`useAudioDevice failed: ${result.errorCode} ${result.extendedData}`);
      }
      this.currentMicDeviceID.set(resolvedId);
    });
  }

  /**
   * Safari không có thiết bị ảo `default` như Chrome, SDK sẽ báo không tìm thấy thiết bị.
   * Khi đó lấy thiết bị đứng đầu danh sách, chính là thiết bị mặc định của hệ thống.
   */
  private async resolveDeviceId(kind: MediaDeviceKind, deviceId: string): Promise<string> {
    const devices = (await navigator.mediaDevices.enumerateDevices()).filter(
      (device) => device.kind === kind,
    );
    if (devices.some((device) => device.deviceId === deviceId)) return deviceId;
    return devices[0]?.deviceId || deviceId;
  }

  /** Safari mute track cũ khi có lệnh getUserMedia mới cùng loại, nên không cho 2 lần đổi thiết bị chạy song song. */
  private deviceSwitchQueue: Promise<unknown> = Promise.resolve();

  private enqueueDeviceSwitch<T>(task: () => Promise<T>): Promise<T> {
    const run = this.deviceSwitchQueue.then(task, task);
    this.deviceSwitchQueue = run.catch(() => undefined);
    return run;
  }

  isLocalMicTrackEnded(): boolean {
    const audioTrack = this.localStream()?.stream.getAudioTracks()[0];
    return audioTrack?.readyState === 'ended';
  }

  async useCamDevice(deviceId: string) {
    return this.enqueueDeviceSwitch(async () => {
      const stream = this.localStream()?.stream;
      if (!stream) return;

      const resolvedId = await this.resolveDeviceId('videoinput', deviceId);
      const result = await this.zegoEngineService.engine.useVideoDevice(stream, resolvedId);
      if (result.errorCode !== 0) {
        throw new Error(`useVideoDevice failed: ${result.errorCode} ${result.extendedData}`);
      }
      this.currentCamDeviceID.set(resolvedId);
    });
  }

  /** Truyền null khi rời họp để call không kế thừa preset của meeting. */
  setVideoPreset(preset: VideoPreset | null) {
    this.videoPreset = preset;
    void this.applyVideoPreset();
  }

  private async applyVideoPreset() {
    const stream = this.localStream()?.stream;
    if (!this.videoPreset || !stream || !stream.getVideoTracks().length) return;
    if (!this.isEngineValid()) return;

    try {
      await this.engine()!.setVideoConfig(stream, { ...this.videoPreset });
    } catch (error) {
      console.error('Error applying video preset:', error);
    }
  }

  /**
   * Thêm track mic/cam vào stream đang publish, không stop rồi publish lại.
   *
   * `startCapture*` chỉ đưa track mới vào đúng instance ZegoLocalStream đang có, còn
   * `updatePublishingStream` renegotiate riêng track đó nên track còn lại không bị chạm tới:
   * thêm mic không làm giật hình, thêm cam không làm ngắt tiếng.
   *
   * @returns false khi không update được, caller cần fallback về stop + republish.
   */
  async addTrackToPublishingStream(type: 'audio' | 'video'): Promise<boolean> {
    const current = this.localStream();
    if (!current) return false;
    if (!this.isEngineValid()) {
      console.warn('[ZegoStream] Cannot update publishing stream - engine unavailable');
      return false;
    }

    const engine = this.engine()!;
    const stream = current.stream;

    try {
      // Truyền bản copy: config là object dùng chung, không để SDK ghi đè lên nó.
      if (type === 'audio') {
        await stream.startCaptureMicrophone({ ...MICROPHONE_AUDIO_CONFIG });
      } else {
        await stream.startCaptureCamera({ ...CAMERA_VIDEO_CONFIG });
      }

      // Track vừa capture chưa nằm trong luồng đang publish cho tới khi gọi update.
      await engine.updatePublishingStream(stream, STREAM_UPDATE_TYPE[type]);

      if (type === 'audio') {
        engine.muteMicrophone(false);
      } else {
        await this.applyVideoPreset();
      }

      this.setCurrentDeviceId(stream);
      return true;
    } catch (error) {
      console.error(`Error adding ${type} track to publishing stream:`, error);
      return false;
    }
  }

  /**
   * Bỏ track mic/cam khỏi stream đang publish, không stop rồi publish lại.
   *
   * Đối xứng với [addTrackToPublishingStream]: chỉ track bị bỏ được renegotiate nên track
   * còn lại không bị ngắt (mất quyền mic khi đang bật cam thì hình không giật).
   *
   * @returns false khi không update được, caller cần fallback về stop + republish.
   */
  async removeTrackFromPublishingStream(type: 'audio' | 'video'): Promise<boolean> {
    const current = this.localStream();
    if (!current) return false;
    if (!this.isEngineValid()) {
      console.warn('[ZegoStream] Cannot update publishing stream - engine unavailable');
      return false;
    }

    const engine = this.engine()!;
    const stream = current.stream;

    try {
      const isStopped =
        type === 'audio' ? await stream.stopCaptureAudio() : await stream.stopCaptureVideo();
      // Không dừng được capture thì đừng renegotiate, để caller publish lại cho chắc.
      if (!isStopped) return false;

      await engine.updatePublishingStream(stream, STREAM_UPDATE_TYPE[type]);

      this.setCurrentDeviceId(stream);
      return true;
    } catch (error) {
      console.error(`Error removing ${type} track from publishing stream:`, error);
      return false;
    }
  }

  /**
   * extraInfo chỉ được gắn một lần lúc startPublishingStream, nên khi thêm/bỏ track bằng
   * updatePublishingStream (không republish) phải cập nhật lại để phía nhận không đọc
   * trạng thái mic/cam cũ.
   */
  async updatePublishExtraInfo(isMicEnabled: boolean, isCameraEnabled: boolean): Promise<void> {
    const current = this.localStream();
    if (!current || !this.isEngineValid()) return;

    try {
      await this.engine()!.setStreamExtraInfo(
        current.streamID,
        JSON.stringify({ isMicEnabled, isCameraEnabled }),
      );
    } catch (error) {
      console.error('Error updating stream extra info:', error);
    }
  }

  stopPublishingStream(streamID: string, localStream?: ZegoLocalStream): void {
    console.log('DEBUG-CALL', 'stopPublishingStream', streamID);

    if (!this.isEngineValid()) {
      console.warn('[ZegoStream] Cannot stop publishing stream - engine unavailable');
      return;
    }
    try {
      this.engine()!.stopPublishingStream(streamID);
      if (localStream) {
        console.log('DEBUG-CALL', 'destroy local stream', streamID);
        this.engine()!.destroyStream(localStream);
      }
      if (this.localStream()?.streamID === streamID) {
        console.log('DEBUG-CALL', 'clear local stream state', streamID);
        this.localStream.set(null);
      }
    } catch (error) {
      console.error('Error stopping stream:', error);
      throw error;
    }
  }

  async playStreamToDOM(
    stream: string | MediaStream,
    selector: string,
    objectFit: 'cover' | 'contain' = 'cover',
  ): Promise<MediaStream | null> {
    const MAX_ELEMENT_LOOKUP_ATTEMPTS = 3;
    const RETRY_DELAY_MS = 2000;
    const engine = this.engine();
    if (!engine) return null;
    try {
      const remoteStream =
        typeof stream === 'string' ? await engine.startPlayingStream(stream) : stream;
      await retry(
        () =>
          new Promise<void>((resolve, reject) => {
            setTimeout(() => {
              let screenRemoteElement = document.getElementById(selector);
              if (window.documentPictureInPicture?.window) {
                screenRemoteElement =
                  window.documentPictureInPicture.window.document.getElementById(selector);
              }
              if (!screenRemoteElement) {
                reject(new Error('VIDEO_ELEMENT_NOT_FOUND'));
                return;
              }
              try {
                const remoteView = engine.createRemoteStreamView(remoteStream);
                remoteView.play(screenRemoteElement, {
                  enableAutoplayDialog: false,
                  objectFit,
                });
                const video = screenRemoteElement.querySelector('video');

                if (video) {
                  resolve();
                }
                reject(new Error('VIDEO_ELEMENT_NOT_FOUND'));
              } catch (err) {
                reject(err);
              }
            });
          }),
        {
          maxAttempts: MAX_ELEMENT_LOOKUP_ATTEMPTS,
          delayMs: RETRY_DELAY_MS,
          onRetry: (error, attempt) => {
            if (error instanceof Error && error.message === 'VIDEO_ELEMENT_NOT_FOUND') {
              console.warn(
                `Video element not found (${attempt}/${MAX_ELEMENT_LOOKUP_ATTEMPTS}), retrying:`,
                selector,
              );
            }
          },
          shouldRetry: (error) =>
            error instanceof Error && error.message === 'VIDEO_ELEMENT_NOT_FOUND',
        },
      );
      return remoteStream;
    } catch (err) {
      console.error('Play stream error', err);
      return null;
    }
  }

  private isScreenStreamID(streamID: string): boolean {
    return streamID.endsWith('_screen');
  }

  addStream(streamInfo: ZegoStreamList) {
    const streamID = streamInfo.streamID;
    const targetMap = this.isScreenStreamID(streamID) ? this.screenSharedStreams : this.mapStream;
    targetMap.update((ms) => {
      const newMap = new Map(ms);
      newMap.set(streamID, streamInfo);
      return newMap;
    });
  }

  removeStream(streamID: string) {
    const targetMap = this.isScreenStreamID(streamID) ? this.screenSharedStreams : this.mapStream;
    targetMap.update((ms) => {
      const newMap = new Map(ms);
      newMap.delete(streamID);
      return newMap;
    });
  }

  clearStreams(): void {
    this.mapStream.set(new Map());
    this.screenSharedStreams.set(new Map());
  }
}
